import { Store } from 'components/common/types/Store.types';
import PlannerSecondaryPanel from 'components/pages/Planner/PlannerSecondaryPanel';
import { useCategoryOptions } from 'customHooks/useCategoryOptions';
import { canDisplayOptionResults } from 'components/pages/Planner/PlannerSecondaryPanel/utils';
import { isEqual } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { hidePlannerSecondaryPanel } from 'store/dealManagement/reducer';
import { calculateSecondaryPanelPosition } from 'components/pages/Planner/PlannerLineActions/calculateSecondaryPanelPosition';
import { updateIsObjectiveSubsectionChange } from 'store/objective/reducer';
import { CircularLoadingSpinner } from 'lib/CircularLoadingSpinner/CircularLoadingSpinner';
import Search from 'lib/Search';
import TagsOptionsList from 'components/common/TagsOptionsList/TagsOptionsList';
import { TagsProps } from './Tags.types';

const Tags = ({
  tagOptions,
  selectedTags,
  onConfirm,
  cancelButtonLabel,
  confirmButtonLabel,
  isSubsection = false,
}: TagsProps): React.ReactElement => {
  const dispatch = useDispatch();
  const isFetchingReferences = useSelector((state: Store) => state.dealManagement.isFetchingReferences);

  const {
    onCategoryDirectionSort,
    searchText,
    openCategories,
    onFilterTextChange,
    selectedOptions: selectedDealLineTags,
    filteredOptions,
    getNumberOfFilteredOptions,
    sortDirection,
    includedOptions,
    excludedOptions,
    isOptionSelected,
    isOptionIncluded,
    onOptionIncludeExcludeChange,
    onToggleCategory,
    setSelectedOptions,
    onCategoryIncludeAll,
    onCategoryExcludeAll,
    getCategoryIncludeState,
  } = useCategoryOptions({ options: tagOptions, initialSelectedOptions: selectedTags });

  const isChange = useMemo(() => !isEqual(selectedTags, selectedDealLineTags), [selectedTags, selectedDealLineTags]);

  useEffect(() => {
    if (isSubsection) dispatch(updateIsObjectiveSubsectionChange(isChange));
  }, [isChange, isSubsection]);

  return (
    <PlannerSecondaryPanel
      title="Tags"
      onSubmit={() => onConfirm(selectedDealLineTags)}
      onCancel={() => dispatch(hidePlannerSecondaryPanel())}
      cancelButtonLabel={cancelButtonLabel}
      confirmButtonLabel={confirmButtonLabel}
      isDisabled={!isChange}
      isSubsection={isSubsection}
      showBackButton={isSubsection}
      isChange={isChange}
      pendoId="Tags"
      customStyles={calculateSecondaryPanelPosition({ width: 400 })}
    >
      {isFetchingReferences ? (
        <CircularLoadingSpinner />
      ) : (
        <>
          <div className="mb-2">
            <Search value={searchText} placeholder="Search tags" onChange={onFilterTextChange} />
          </div>

          {canDisplayOptionResults(filteredOptions) ? (
            <TagsOptionsList
              filteredOptions={filteredOptions}
              selectedOptions={selectedDealLineTags}
              openCategories={openCategories}
              sortDirection={sortDirection}
              includedOptions={includedOptions}
              excludedOptions={excludedOptions}
              onSort={onCategoryDirectionSort}
              onToggleCategory={onToggleCategory}
              setSelectedOptions={setSelectedOptions}
              getNumberOfFilteredOptions={getNumberOfFilteredOptions}
              isOptionSelected={isOptionSelected}
              isOptionIncluded={isOptionIncluded}
              onOptionIncludeExcludeChange={onOptionIncludeExcludeChange}
              onCategoryIncludeAll={onCategoryIncludeAll}
              onCategoryExcludeAll={onCategoryExcludeAll}
              getCategoryIncludeState={getCategoryIncludeState}
            />
          ) : (
            <p>There are no results matching your search</p>
          )}
        </>
      )}
    </PlannerSecondaryPanel>
  );
};

export default Tags;
